<script setup lang="ts">
type Props = {
  label: string;
  required?: boolean;
  badgeType?: 'success' | 'error';
  badgeLabel?: string;
  actionLabel?: string;
  actionType?: 'edit' | 'view';
};

defineEmits<{ action: [] }>();

const {
  label,
  required = false,
  badgeType,
  badgeLabel,
  actionLabel,
  actionType,
} = defineProps<Props>();
</script>

<template>
  <div :class="$style.card">
    <div :class="$style.left">
      <div :class="$style.labelRow">
        <span :class="$style.label">
          {{ label }}<span v-if="required">*</span>
        </span>
        <div
          v-if="badgeType && badgeLabel"
          :class="[
            $style.badge,
            badgeType === 'success' ? $style.badgeSuccess : $style.badgeError,
          ]"
        >
          <svg
            v-if="badgeType === 'error'"
            fill="none"
            height="5"
            viewBox="0 0 5 5"
            width="5"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.63922 1.07915L5 0.719962L4.28004 0L3.92085 0.359186L2.5 1.78004L1.07915 0.359186L0.719962 0L0 0.719962L0.359186 1.07915L1.78004 2.5L0.360776 3.92085L0 4.28004L0.719962 5L1.07915 4.64081L2.5 3.21996L3.92085 4.64081L4.28004 5L5 4.28004L4.64081 3.92085L3.21996 2.5L4.64081 1.07915H4.63922Z"
              fill="white"
            />
          </svg>
          <svg
            v-else
            fill="none"
            height="5"
            viewBox="0 0 7 5"
            width="7"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 0.726335L6.63742 1.06998L2.82876 4.6751L2.48493 5L2.1411 4.6751L0.362581 2.99281L0 2.64917L0.687653 1.92284L1.05023 2.26648L2.48493 3.62387L5.94977 0.343643L6.31235 0L7 0.726335Z"
              fill="white"
            />
          </svg>
          <span :class="$style.badgeText">{{ badgeLabel }}</span>
        </div>
      </div>
      <div :class="$style.value">
        <slot />
      </div>
    </div>
    <button
      v-if="actionLabel"
      :class="$style.action"
      type="button"
      @click="$emit('action')"
    >
      <span class="uppercase">{{ actionLabel }}</span>
      <NuxtIcon
        v-if="actionType === 'edit'"
        :class="$style.actionIcon"
        name="user/edit"
      />
      <NuxtIcon
        v-else-if="actionType === 'view'"
        :class="$style.actionIcon"
        name="user/eye"
      />
    </button>
  </div>
</template>

<style module lang="scss">
.card {
  display: flex;
  flex-grow: var(--card-grow, 0);
  padding: 10px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 10px;
  border: 1px solid #00c9ff;
  background: rgba(0, 201, 255, 0.05);
}

.left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  align-self: stretch;
  border-radius: 10px;
}

.labelRow {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
}

.label {
  color: #00c9ff;
  font-size: 10px;
  font-weight: 600;
  line-height: 13px;

  span {
    @apply text-error;
  }
}

.badge {
  display: flex;
  padding: 2px 3px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: 6px;
}

.badgeError {
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #dd3e3e 0%,
    #8b1a1a 100%
  );
}

.badgeSuccess {
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #73dd3e 0%,
    #2b8b1a 100%
  );
}

.badgeText {
  color: #fff;
  padding: 1px 4px;
  font-size: 9px;
  font-weight: 600;
  line-height: 6px;
}

.value {
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.action {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  cursor: pointer;
  color: #00c9ff;
  font-size: 9px;
  font-weight: 600;
  line-height: 11px;
  text-transform: none;
}

.actionIcon {
  display: flex;
  align-items: center;

  :deep(svg) {
    width: 11px;
    height: 11px;
  }
}
</style>
